import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { fromJS } from 'immutable'

import { TimeField } from 'forms/fields'
import styles from './styles.css'

function calculateIsTooShortOccasion(valueA, valueB) {
  return (
    moment(valueA, 'HH:mm').diff(moment(valueB, 'HH:mm'), 'minutes') / 60 < 2
  )
}

export default class OccasionPopup extends React.Component {
  static propTypes = {
    onChange: PropTypes.func.isRequired,
    day: PropTypes.instanceOf(Date).isRequired,
    schedule: PropTypes.arrayOf(PropTypes.object).isRequired,
    calendar: PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props)
    this.wrapperRef = React.createRef()

    const { schedule, day } = props

    this.state = {
      isOutsideViewport: false,
    }

    this.container = React.createRef()

    const currentAvailableDate = schedule.find(d =>
      moment(d.start_dt).isSame(day, 'day'),
    )
    const halfHours = Math.floor(
      moment(currentAvailableDate.end_dt).diff(
        currentAvailableDate.start_dt,
        'minutes',
      ) / 30,
    )

    this.fromHours = [...Array(halfHours + 1).keys()]
      .map(i =>
        moment(currentAvailableDate.start_dt)
          .add(i * 0.5, 'hours')
          .format('HH:mm:00'),
      )
      .slice(0, -4)

    this.toHours = [...Array(halfHours + 1).keys()]
      .map(i =>
        moment(currentAvailableDate.start_dt)
          .add(i * 0.5, 'hours')
          .format('HH:mm:00'),
      )
      .slice(4)

    // During the night when switching from daylight savings time to
    // standard time, "02:00" and "02:30" are generated twice, since they
    // occur twice. One _could_ mark them as "02:00 (sommartid)" and "02:00
    // (vintertid)" to disambiguate, but that’s unnecessarily complicated
    // since in practice there will be no activities in the middle of the
    // night anyway. Instead, filter out any duplicates since the rest of
    // the code assumes that all times are unique. This prevents issues
    // during development (it’s easy to create a “bad” schedule by mistake).
    // (When switching from standard time to daylight savings time some
    // hours might go missing, but it doesn’t matter.)
    this.fromHours = [...new Set(this.fromHours)]
    this.toHours = [...new Set(this.toHours)]
  }

  componentDidMount() {
    const viewportWidth = document.documentElement.clientWidth
    const container = this.container.current
    const rect = container.getBoundingClientRect()

    const componentLeftPos = rect.left
    const componentWidth = rect.width

    if (componentLeftPos + componentWidth > viewportWidth) {
      // eslint-disable-next-line react/no-did-mount-set-state
      this.setState({
        isOutsideViewport: true,
      })
    }

    this.wrapperRef.current.focus()

    document.addEventListener('mousedown', this.closeOnOutside, true)
    document.addEventListener('focus', this.closeOnOutside, true)
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.closeOnOutside, true)
    document.removeEventListener('focus', this.closeOnOutside, true)
  }

  getFromAndToValues() {
    const { day, calendar } = this.props
    const { instances = [], fromTime, toTime } = calendar

    // Selected and added
    const selected =
      instances && instances.find(d => moment(d.from_dt).isSame(day, 'day'))
    const selectedFrom = selected && moment(selected.from_dt).format('HH:mm:00')
    const selectedTo = selected && moment(selected.to_dt).format('HH:mm:00')

    // Default to the first of the selected instances
    const from =
      instances.length > 0 && moment(instances[0].from_dt).format('HH:mm:00')
    const to =
      instances.length > 0 && moment(instances[0].to_dt).format('HH:mm:00')

    return {
      from: fromTime || selectedFrom || from || this.fromHours[0],
      to: toTime || selectedTo || to || this.toHours[0],
    }
  }

  closeOnOutside = e => {
    const node = this.wrapperRef.current

    if (!node.contains(e.target)) {
      this.close(e)
    }
  }

  saveDateTime() {
    const { day, onChange, schedule, calendar } = this.props
    const { instances = [], month } = calendar

    let { from, to } = this.getFromAndToValues()

    // Make sure that the from time is correct for the given day.
    if (
      this.fromHours.indexOf(from.slice(0, 8)) === -1 ||
      this.toHours.indexOf(to.slice(0, 8)) === -1
    ) {
      ;[from] = this.fromHours
      ;[to] = this.toHours
    }

    const newOccasion = {
      from_dt: moment(day)
        .hours(from.slice(0, 2))
        .minutes(from.slice(3, 5))
        .toDate(),
      to_dt: moment(day)
        .hours(to.slice(0, 2))
        .minutes(to.slice(3, 5))
        .toDate(),
    }

    const existingIndex = instances.findIndex(d =>
      moment(d.from_dt).isSame(day, 'day'),
    )
    const newInstances =
      existingIndex !== -1
        ? // If we’re updating the time for a previously selected day,
          // replace the previous with the new.
          [
            ...instances.slice(0, existingIndex),
            newOccasion,
            ...instances.slice(existingIndex + 1),
          ]
        : // Otherwise add the new, and sort to keep them chronological.
          [...instances, newOccasion].sort((a, b) =>
            moment(a.start_dt).diff(b.start_dt),
          )

    // Try to jump to a suitable next day.
    const nextDays =
      // Out of all the available days in the leader’s schedule (the blue days)…
      schedule
        // …remove days we’ve alreay selected (the user can select
        // days out of order)…
        .filter(
          d =>
            !newInstances.some(v =>
              moment(v.from_dt).isSame(d.start_dt, 'day'),
            ),
        )
        // …and sort to get the best suggestion. Better suggestions first.
        .sort((a, b) => {
          // Prefer days _after_ the day this popup belongs to.
          const isAfter = d => moment(d).isAfter(day, 'day')
          const aAfter = isAfter(a.start_dt)
          const bAfter = isAfter(b.start_dt)
          if (aAfter && !bAfter) {
            return -1
          }
          if (!aAfter && bAfter) {
            return 1
          }

          // Ideally we want to find a day exactly one week away. These
          // diffs represent the number of days from that target. 9 days
          // away from the day of this popup is +2, 5 days away is -2.
          // By default, moment `Math.floor`s the diff. Pass `true` to
          // get a decimal value and `Math.round` to round like a
          // human.
          const diff = d => Math.round(moment(d).diff(day, 'days', true)) - 7
          const aDiff = diff(a.start_dt)
          const bDiff = diff(b.start_dt)

          // Prefer days closer to the target (one week away), regardless
          // of direction (earlier or later).
          const comparison = Math.abs(aDiff) - Math.abs(bDiff)
          if (comparison !== 0) {
            return comparison
          }

          // If at the same distance, prefer later days.
          if (aDiff > 0 && bDiff < 0) {
            return -1
          }
          if (aDiff < 0 && bDiff > 0) {
            return 1
          }

          // Finally, sort chronologically. This should never happen
          // because every day should be unique.
          return moment(a.start_dt).diff(b.start_dt)
        })

    let nextDate = null
    let nextFromTime = null
    let nextToTime = null

    if (nextDays.length !== 0) {
      const nextDay = nextDays[0]
      nextDate = moment(nextDay.start_dt).toDate()

      const halfHours = Math.floor(
        moment(nextDay.end_dt).diff(nextDay.start_dt, 'minutes') / 30,
      )
      const availableHours = [...Array(halfHours + 1).keys()].map(i =>
        moment(nextDay.start_dt)
          .add(i * 0.5, 'hours')
          .format('HH:mm:00'),
      )

      // Select a most suitable hour based on previous choice.
      let targetFromIndex = availableHours.indexOf(from)
      targetFromIndex = targetFromIndex !== -1 ? targetFromIndex : 0
      let targetToIndex = availableHours.indexOf(to)
      targetToIndex =
        targetToIndex !== -1 ? Math.max(targetToIndex, targetFromIndex + 4) : 4

      nextFromTime = availableHours[targetFromIndex]
      // Minimum activity duration is 2 hours so we add 4 half hours.
      // TODO: Do not hardocde this value.
      nextToTime = availableHours[targetToIndex]
    }

    onChange(
      fromJS({
        ...calendar,
        instances: newInstances,
        selectedDay: nextDate,
        month: nextDate || month,
        fromTime: nextFromTime,
        toTime: nextToTime,
      }),
    )
  }

  remove() {
    const { day, calendar, onChange } = this.props
    const { instances } = calendar
    const index = instances.findIndex(d => moment(d.from_dt).isSame(day, 'day'))

    if (index === -1) {
      return
    }
    console.log(instances)
    onChange(
      fromJS({
        ...calendar,
        instances: [
          ...instances.slice(0, index),
          ...instances.slice(index + 1),
        ],
      }),
    )
  }

  close(e) {
    e.preventDefault()
    const { calendar, onChange } = this.props
    onChange(
      fromJS({
        ...calendar,
        selectedDay: null,
        fromTime: null,
        toTime: null,
      }),
    )
  }

  render() {
    const { day, calendar, onChange } = this.props
    const { instances = [] } = calendar

    const { from: fromValue, to: toValue } = this.getFromAndToValues()

    const existingIndex = instances.findIndex(d =>
      moment(d.from_dt).isSame(day, 'day'),
    )

    const updateExistingInstanceTime = (
      timeString,
      type,
      isTooShortOccasion,
    ) => {
      const { id, ...current } = instances[existingIndex]
      const time = moment(timeString, 'HH:mm')
      const newDate = moment(current.from_dt)
        .hour(time.hour())
        .minute(time.minute())
        .toDate()
      console.log(
        'is too short',
        isTooShortOccasion,
        newDate,
        fromValue,
        toValue,
      )
      onChange(
        fromJS({
          ...calendar,
          instances: [
            ...instances.slice(0, existingIndex),
            {
              ...current,
              [type]: newDate,
              ...(type === 'to_dt' && isTooShortOccasion
                ? {
                    from_dt: moment(newDate)
                      .subtract(2, 'hour')
                      .toDate(),
                  }
                : {}),
              ...(type === 'from_dt' && isTooShortOccasion
                ? {
                    to_dt: moment(newDate)
                      .add(2, 'hour')
                      .toDate(),
                  }
                : {}),
            },
            ...instances.slice(existingIndex + 1),
          ],
        }),
      )
    }

    return (
      <div tabIndex="-1" ref={this.wrapperRef} className={styles.dayModal}>
        <div
          ref={this.container}
          className={
            this.state.isOutsideViewport
              ? styles.containerLeft
              : styles.containerRight
          }
        >
          <button className={styles.close} onClick={e => this.close(e)} />

          {instances.length < 1 && (
            <p className={styles.info}>Du måste välja minst 1 tillfälle</p>
          )}

          <TimeField
            hours={this.fromHours}
            onChange={e => {
              const { value } = e.target
              const isTooShortOccasion =
                toValue && calculateIsTooShortOccasion(toValue, value)

              if (existingIndex !== -1) {
                updateExistingInstanceTime(value, 'from_dt', isTooShortOccasion)
                return
              }

              const time = moment(value, 'HH:mm')

              onChange(
                fromJS({
                  ...calendar,
                  fromTime: value,
                  ...(isTooShortOccasion
                    ? {
                        toTime: moment(day)
                          .hour(time.hour())
                          .minute(time.minute())
                          .add(2, 'hour')
                          .format('HH:mm:00'),
                      }
                    : {}),
                }),
              )
            }}
            value={fromValue}
            label="Börjar"
          />
          <TimeField
            hours={this.toHours}
            onChange={e => {
              const { value } = e.target
              const isTooShortOccasion =
                fromValue && calculateIsTooShortOccasion(value, fromValue)

              if (existingIndex !== -1) {
                updateExistingInstanceTime(value, 'to_dt', isTooShortOccasion)
                return
              }

              const time = moment(value, 'HH:mm')

              onChange(
                fromJS({
                  ...calendar,
                  toTime: value,
                  ...(isTooShortOccasion
                    ? {
                        fromTime: moment(day)
                          .hour(time.hour())
                          .minute(time.minute())
                          .subtract(2, 'hour')
                          .format('HH:mm:00'),
                      }
                    : {}),
                }),
              )
            }}
            value={toValue}
            label="Slutar"
          />

          {existingIndex === -1 ? (
            <div className={styles.buttonFooter}>
              <button onClick={() => this.saveDateTime()}>Lägg till</button>
            </div>
          ) : (
            <div className={styles.buttonFooter}>
              <button onClick={() => this.remove()}>Ta bort</button>
            </div>
          )}
        </div>
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ../source/components/OccasionPopup/index.js